/**
 * Proof Upload Modal Component
 *
 * Allows athletes to submit proof for drills that require video.
 * Supports both uploading a new video and selecting an existing one.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import {
  listVideos,
  requestUploadUrl,
  uploadToS3,
  completeUpload,
  submitProof,
  listSubmissions,
  generateVideoTitle,
  type Video,
  type DrillSubmission,
} from '@tss/shared';
import { formatRelativeTime } from '@tss/shared';
import {
  X,
  Upload,
  Video as VideoIcon,
  CheckCircle,
  Loader2,
  Play,
  ArrowLeft,
  Camera,
  Square,
  RotateCcw,
  Send,
} from 'lucide-react';

interface ProofUploadModalProps {
  programId: string;
  moduleId: string;
  drillIndex: number;
  drillName: string;
  orgId: string;
  proxyAthleteId?: string;  // For guardian proxy mode
  onClose: () => void;
  onSubmitted: () => void;
}

export function ProofUploadModal({
  programId,
  moduleId,
  drillIndex,
  drillName,
  orgId,
  proxyAthleteId,
  onClose,
  onSubmitted,
}: ProofUploadModalProps) {
  const [mode, setMode] = useState<'choose' | 'upload' | 'select' | 'record'>('choose');
  const [videos, setVideos] = useState<Video[]>([]);
  const [existingSubmission, setExistingSubmission] = useState<DrillSubmission | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Upload state
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Recording state
  const videoRef = useRef<HTMLVideoElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const [isRecording, setIsRecording] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const [recordedUrl, setRecordedUrl] = useState<string | null>(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [cameraReady, setCameraReady] = useState(false);
  const recordingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    setLoading(true);
    try {
      // Load existing videos and check for existing submission
      const [videosData, submissionsData] = await Promise.all([
        listVideos(),
        listSubmissions(programId, { module_id: moduleId }),
      ]);

      setVideos(videosData.videos.filter(v => v.status === 'READY'));

      // Check if there's already a submission for this drill
      const existing = submissionsData.submissions.find(
        s => s.module_id === moduleId && s.drill_index === drillIndex
      );
      setExistingSubmission(existing || null);
    } catch (err) {
      console.error('Failed to load data:', err);
      setError('Failed to load videos');
    } finally {
      setLoading(false);
    }
  }

  // Camera/Recording functions
  const startCamera = useCallback(async () => {
    try {
      setError(null);
      setCameraReady(false);

      // Request camera access - prefer back camera on mobile
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: true,
      });

      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.muted = true; // Mute preview to avoid feedback
        await videoRef.current.play();
      }

      setCameraReady(true);
    } catch (err) {
      console.error('Camera access failed:', err);
      setError('Could not access camera. Please check permissions.');
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setCameraReady(false);

    // Clear recording timer
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
  }, []);

  const startRecording = useCallback(() => {
    if (!streamRef.current) return;

    chunksRef.current = [];
    setRecordingTime(0);

    const mediaRecorder = new MediaRecorder(streamRef.current, {
      mimeType: MediaRecorder.isTypeSupported('video/webm;codecs=vp9')
        ? 'video/webm;codecs=vp9'
        : 'video/webm'
    });

    mediaRecorder.ondataavailable = (e) => {
      if (e.data.size > 0) {
        chunksRef.current.push(e.data);
      }
    };

    mediaRecorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: 'video/webm' });
      setRecordedBlob(blob);
      setRecordedUrl(URL.createObjectURL(blob));
      stopCamera();
    };

    mediaRecorderRef.current = mediaRecorder;
    mediaRecorder.start(1000); // Collect data every second
    setIsRecording(true);

    // Start timer
    recordingTimerRef.current = setInterval(() => {
      setRecordingTime(prev => prev + 1);
    }, 1000);
  }, [stopCamera]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);

      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
    }
  }, [isRecording]);

  const resetRecording = useCallback(() => {
    if (recordedUrl) {
      URL.revokeObjectURL(recordedUrl);
    }
    setRecordedBlob(null);
    setRecordedUrl(null);
    setRecordingTime(0);
    startCamera();
  }, [recordedUrl, startCamera]);

  // Cleanup on unmount or mode change
  useEffect(() => {
    if (mode === 'record') {
      startCamera();
    }
    return () => {
      stopCamera();
      if (recordedUrl) {
        URL.revokeObjectURL(recordedUrl);
      }
    };
  }, [mode]);

  // Format recording time as MM:SS
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  async function handleRecordedSubmit() {
    if (!recordedBlob) return;

    setUploading(true);
    setError(null);
    setUploadProgress(0);

    try {
      const recordingTitle = title || `${drillName} - Recording`;
      const filename = `recording-${Date.now()}.webm`;

      // Step 1: Request upload URL
      const { upload_url, video_id, fields } = await requestUploadUrl({
        org_id: orgId,
        filename,
        file_size: recordedBlob.size,
        title: recordingTitle,
        description: `Proof for: ${drillName}`,
        athlete_id: proxyAthleteId,  // For guardian proxy mode
      });

      // Step 2: Upload to S3
      await uploadToS3(upload_url, fields, recordedBlob, (percent) => {
        setUploadProgress(percent);
      });

      // Step 3: Complete upload
      await completeUpload(video_id);

      // Step 4: Submit as proof
      await submitProof(programId, {
        module_id: moduleId,
        drill_index: drillIndex,
        drill_name: drillName,
        video_id,
      });

      onSubmitted();
    } catch (err) {
      console.error('Upload failed:', err);
      setError(err instanceof Error ? err.message : 'Upload failed. Please try again.');
      setUploading(false);
    }
  }

  function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (!selectedFile.type.startsWith('video/')) {
        setError('Please select a video file');
        return;
      }
      if (selectedFile.size > 500 * 1024 * 1024) {
        setError('File size must be less than 500MB');
        return;
      }
      setFile(selectedFile);
      setError(null);
      // Auto-fill title from filename with smart cleanup
      if (!title) {
        setTitle(generateVideoTitle(selectedFile.name));
      }
    }
  }

  async function handleUploadAndSubmit() {
    if (!file || !title) return;

    setUploading(true);
    setError(null);
    setUploadProgress(0);

    try {
      // Step 1: Request upload URL
      const { upload_url, video_id, fields } = await requestUploadUrl({
        org_id: orgId,
        filename: file.name,
        file_size: file.size,
        title,
        description: `Proof for: ${drillName}`,
        athlete_id: proxyAthleteId,  // For guardian proxy mode
      });

      // Step 2: Upload to S3
      await uploadToS3(upload_url, fields, file, (percent) => {
        setUploadProgress(percent);
      });

      // Step 3: Complete upload
      await completeUpload(video_id);

      // Step 4: Submit as proof (video will be ready soon)
      await submitProof(programId, {
        module_id: moduleId,
        drill_index: drillIndex,
        drill_name: drillName,
        video_id,
      });

      onSubmitted();
    } catch (err) {
      console.error('Upload failed:', err);
      setError(err instanceof Error ? err.message : 'Upload failed. Please try again.');
      setUploading(false);
    }
  }

  async function handleSelectVideo(videoId: string) {
    setSubmitting(true);
    setError(null);

    try {
      await submitProof(programId, {
        module_id: moduleId,
        drill_index: drillIndex,
        drill_name: drillName,
        video_id: videoId,
      });
      onSubmitted();
    } catch (err) {
      console.error('Submit proof failed:', err);
      setError(err instanceof Error ? err.message : 'Failed to submit proof.');
      setSubmitting(false);
    }
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        className="card"
        style={{
          width: '100%',
          maxWidth: 'min(500px, calc(100vw - 32px))',
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: '20px 24px',
            borderBottom: '1px solid var(--gray-100)',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          {mode !== 'choose' && (
            <button
              onClick={() => setMode('choose')}
              className="btn-ghost"
              style={{ padding: '8px', boxShadow: 'none' }}
            >
              <ArrowLeft size={18} />
            </button>
          )}
          <div style={{ flex: 1 }}>
            <h2 style={{ fontSize: '18px', fontWeight: '600', color: 'var(--gray-900)' }}>
              {mode === 'choose' ? 'Submit Proof' : mode === 'upload' ? 'Upload Video' : mode === 'record' ? 'Record Video' : 'Select Video'}
            </h2>
            <p style={{ fontSize: '13px', color: 'var(--gray-500)', marginTop: '2px' }}>
              {drillName}
            </p>
          </div>
          <button
            onClick={onClose}
            className="btn-ghost"
            style={{ padding: '8px', boxShadow: 'none' }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto', padding: '20px 24px' }}>
          {error && (
            <div
              style={{
                padding: '12px 16px',
                background: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: 'var(--radius-md)',
                color: 'var(--error)',
                marginBottom: '16px',
                fontSize: '14px',
              }}
            >
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex-center" style={{ padding: '40px' }}>
              <Loader2 size={32} className="animate-spin" style={{ color: 'var(--accent)' }} />
            </div>
          ) : existingSubmission ? (
            // Show existing submission
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <div
                className="icon-container icon-container-lg"
                style={{ background: 'rgba(249, 115, 22, 0.1)', margin: '0 auto 16px' }}
              >
                <CheckCircle size={32} style={{ color: 'var(--success)' }} />
              </div>
              <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>
                Proof Already Submitted
              </h3>
              <p style={{ fontSize: '14px', color: 'var(--gray-500)', marginBottom: '4px' }}>
                Status: <span style={{ fontWeight: '600', color: existingSubmission.status === 'APPROVED' ? 'var(--success)' : existingSubmission.status === 'NEEDS_WORK' ? 'var(--warning)' : 'var(--accent)' }}>
                  {existingSubmission.status === 'APPROVED' ? 'Approved' : existingSubmission.status === 'NEEDS_WORK' ? 'Needs Work' : 'Pending Review'}
                </span>
              </p>
              {existingSubmission.coach_feedback && (
                <p style={{ fontSize: '13px', color: 'var(--gray-600)', marginTop: '8px', fontStyle: 'italic' }}>
                  "{existingSubmission.coach_feedback}"
                </p>
              )}
            </div>
          ) : mode === 'choose' ? (
            // Choose mode - Record is primary option
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {/* Record Now - Primary option */}
              <button
                onClick={() => setMode('record')}
                className="card card-interactive"
                style={{
                  padding: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  textAlign: 'left',
                  border: '2px solid var(--accent)',
                  background: 'rgba(123, 75, 216, 0.05)',
                }}
              >
                <div
                  className="icon-container icon-container-lg"
                  style={{ background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' }}
                >
                  <Camera size={24} style={{ color: 'white' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <h3 style={{ fontSize: '16px', fontWeight: '600', color: 'var(--gray-900)' }}>
                    Record Now
                  </h3>
                  <p style={{ fontSize: '13px', color: 'var(--gray-500)', marginTop: '2px' }}>
                    Set up your phone and record the drill
                  </p>
                </div>
                <span style={{
                  padding: '4px 8px',
                  background: 'var(--accent)',
                  color: 'white',
                  borderRadius: '12px',
                  fontSize: '11px',
                  fontWeight: '600',
                }}>
                  BEST
                </span>
              </button>

              {/* Divider */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '4px 0' }}>
                <div style={{ flex: 1, height: '1px', background: 'var(--gray-200)' }} />
                <span style={{ fontSize: '12px', color: 'var(--gray-400)' }}>or</span>
                <div style={{ flex: 1, height: '1px', background: 'var(--gray-200)' }} />
              </div>

              <button
                onClick={() => setMode('upload')}
                className="card card-interactive"
                style={{
                  padding: '16px 20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  textAlign: 'left',
                  border: 'none',
                }}
              >
                <div
                  className="icon-container icon-container-md"
                  style={{ background: 'linear-gradient(135deg, var(--accent) 0%, var(--accent-dark) 100%)' }}
                >
                  <Upload size={20} style={{ color: 'white' }} />
                </div>
                <div>
                  <h3 style={{ fontSize: '14px', fontWeight: '600', color: 'var(--gray-900)' }}>
                    Upload Video
                  </h3>
                  <p style={{ fontSize: '12px', color: 'var(--gray-500)', marginTop: '2px' }}>
                    Upload a video you already recorded
                  </p>
                </div>
              </button>

              <button
                onClick={() => setMode('select')}
                className="card card-interactive"
                style={{
                  padding: '16px 20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  textAlign: 'left',
                  border: 'none',
                  opacity: videos.length === 0 ? 0.5 : 1,
                  cursor: videos.length === 0 ? 'not-allowed' : 'pointer',
                }}
                disabled={videos.length === 0}
              >
                <div
                  className="icon-container icon-container-md"
                  style={{ background: 'linear-gradient(135deg, var(--success) 0%, #ea580c 100%)' }}
                >
                  <VideoIcon size={20} style={{ color: 'white' }} />
                </div>
                <div>
                  <h3 style={{ fontSize: '14px', fontWeight: '600', color: 'var(--gray-900)' }}>
                    Select Existing Video
                  </h3>
                  <p style={{ fontSize: '12px', color: 'var(--gray-500)', marginTop: '2px' }}>
                    {videos.length > 0
                      ? `Choose from ${videos.length} uploaded video${videos.length !== 1 ? 's' : ''}`
                      : 'No videos available yet'}
                  </p>
                </div>
              </button>
            </div>
          ) : mode === 'upload' ? (
            // Upload mode
            <div>
              {/* File drop zone */}
              <div
                onClick={() => fileInputRef.current?.click()}
                style={{
                  border: '2px dashed var(--gray-300)',
                  borderRadius: 'var(--radius-md)',
                  padding: '32px 20px',
                  textAlign: 'center',
                  cursor: 'pointer',
                  marginBottom: '20px',
                  background: file ? 'rgba(123, 75, 216, 0.05)' : 'transparent',
                  borderColor: file ? 'var(--accent)' : 'var(--gray-300)',
                }}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="video/*"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
                {file ? (
                  <div>
                    <VideoIcon size={32} style={{ color: 'var(--accent)', marginBottom: '8px' }} />
                    <p style={{ fontWeight: '500', color: 'var(--gray-900)' }}>{file.name}</p>
                    <p style={{ fontSize: '13px', color: 'var(--gray-500)', marginTop: '4px' }}>
                      {(file.size / (1024 * 1024)).toFixed(1)} MB
                    </p>
                  </div>
                ) : (
                  <div>
                    <Upload size={32} style={{ color: 'var(--gray-400)', marginBottom: '8px' }} />
                    <p style={{ fontWeight: '500', color: 'var(--gray-700)' }}>
                      Click to select a video
                    </p>
                    <p style={{ fontSize: '13px', color: 'var(--gray-500)', marginTop: '4px' }}>
                      MP4, MOV, or WebM up to 500MB
                    </p>
                  </div>
                )}
              </div>

              {/* Title field */}
              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label>Title</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder={`Proof: ${drillName}`}
                  disabled={uploading}
                />
              </div>

              {/* Progress bar */}
              {uploading && (
                <div style={{ marginBottom: '20px' }}>
                  <div className="flex-between" style={{ marginBottom: '8px' }}>
                    <span style={{ fontSize: '13px', color: 'var(--gray-600)' }}>Uploading...</span>
                    <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--accent)' }}>{uploadProgress}%</span>
                  </div>
                  <div
                    style={{
                      height: '8px',
                      background: 'var(--gray-200)',
                      borderRadius: '4px',
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        height: '100%',
                        width: `${uploadProgress}%`,
                        background: 'linear-gradient(90deg, var(--accent) 0%, var(--accent-light) 100%)',
                        transition: 'width 0.3s ease',
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Upload button */}
              <button
                onClick={handleUploadAndSubmit}
                disabled={!file || !title || uploading}
                style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
              >
                {uploading ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Uploading & Submitting...
                  </>
                ) : (
                  <>
                    <Upload size={18} />
                    Upload & Submit Proof
                  </>
                )}
              </button>
            </div>
          ) : mode === 'select' ? (
            // Select mode
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {videos.map((video) => (
                <button
                  key={video.video_id}
                  onClick={() => handleSelectVideo(video.video_id)}
                  disabled={submitting}
                  className="card card-interactive"
                  style={{
                    padding: '12px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    textAlign: 'left',
                    border: 'none',
                    opacity: submitting ? 0.7 : 1,
                  }}
                >
                  <div
                    className="flex-center"
                    style={{
                      width: '48px',
                      height: '32px',
                      background: 'linear-gradient(135deg, var(--primary-start) 0%, var(--primary-end) 100%)',
                      borderRadius: 'var(--radius-sm)',
                      flexShrink: 0,
                    }}
                  >
                    <Play size={14} style={{ color: 'white' }} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p className="truncate" style={{ fontWeight: '500', color: 'var(--gray-900)', fontSize: '14px' }}>
                      {video.title}
                    </p>
                    <p style={{ fontSize: '12px', color: 'var(--gray-500)', marginTop: '2px' }}>
                      {formatRelativeTime(video.created_at)}
                    </p>
                  </div>
                  {submitting ? (
                    <Loader2 size={18} className="animate-spin" style={{ color: 'var(--accent)' }} />
                  ) : (
                    <CheckCircle size={18} style={{ color: 'var(--gray-300)' }} />
                  )}
                </button>
              ))}
            </div>
          ) : (
            // Record mode
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Video preview / playback area */}
              <div
                style={{
                  position: 'relative',
                  width: '100%',
                  aspectRatio: '16/9',
                  background: '#000',
                  borderRadius: 'var(--radius-md)',
                  overflow: 'hidden',
                }}
              >
                {recordedUrl ? (
                  // Show recorded video for playback
                  <video
                    src={recordedUrl}
                    controls
                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                  />
                ) : (
                  // Show camera preview
                  <>
                    <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      muted
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                    {!cameraReady && (
                      <div
                        style={{
                          position: 'absolute',
                          inset: 0,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: '#000',
                          color: 'white',
                        }}
                      >
                        <Loader2 size={32} className="animate-spin" style={{ marginBottom: '12px' }} />
                        <p style={{ fontSize: '14px' }}>Starting camera...</p>
                      </div>
                    )}
                    {/* Recording indicator */}
                    {isRecording && (
                      <div
                        style={{
                          position: 'absolute',
                          top: '12px',
                          left: '12px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '6px 12px',
                          background: 'rgba(239, 68, 68, 0.9)',
                          borderRadius: '20px',
                          color: 'white',
                          fontSize: '14px',
                          fontWeight: '600',
                        }}
                      >
                        <div
                          style={{
                            width: '10px',
                            height: '10px',
                            borderRadius: '50%',
                            background: 'white',
                            animation: 'pulse 1s infinite',
                          }}
                        />
                        REC {formatTime(recordingTime)}
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Recording controls */}
              {recordedUrl ? (
                // After recording - show submit/retake buttons
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {/* Title field */}
                  <div className="form-group">
                    <label>Title (optional)</label>
                    <input
                      type="text"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder={`${drillName} - Recording`}
                      disabled={uploading}
                    />
                  </div>

                  {/* Progress bar */}
                  {uploading && (
                    <div>
                      <div className="flex-between" style={{ marginBottom: '8px' }}>
                        <span style={{ fontSize: '13px', color: 'var(--gray-600)' }}>Uploading...</span>
                        <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--accent)' }}>{uploadProgress}%</span>
                      </div>
                      <div
                        style={{
                          height: '8px',
                          background: 'var(--gray-200)',
                          borderRadius: '4px',
                          overflow: 'hidden',
                        }}
                      >
                        <div
                          style={{
                            height: '100%',
                            width: `${uploadProgress}%`,
                            background: 'linear-gradient(90deg, var(--accent) 0%, var(--accent-light) 100%)',
                            transition: 'width 0.3s ease',
                          }}
                        />
                      </div>
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: '12px' }}>
                    <button
                      onClick={resetRecording}
                      disabled={uploading}
                      className="btn-secondary"
                      style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                    >
                      <RotateCcw size={18} />
                      Retake
                    </button>
                    <button
                      onClick={handleRecordedSubmit}
                      disabled={uploading}
                      style={{ flex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                    >
                      {uploading ? (
                        <>
                          <Loader2 size={18} className="animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        <>
                          <Send size={18} />
                          Submit Recording
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ) : (
                // Camera is live - show record controls
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
                  {/* Instructions */}
                  <p style={{ fontSize: '14px', color: 'var(--gray-500)', textAlign: 'center' }}>
                    {isRecording
                      ? 'Recording... Tap stop when done'
                      : 'Position your camera and tap record'}
                  </p>

                  {/* Record/Stop button */}
                  <button
                    onClick={isRecording ? stopRecording : startRecording}
                    disabled={!cameraReady}
                    style={{
                      width: '72px',
                      height: '72px',
                      borderRadius: '50%',
                      border: '4px solid white',
                      background: isRecording ? '#ef4444' : 'transparent',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                      cursor: cameraReady ? 'pointer' : 'not-allowed',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    {isRecording ? (
                      <Square size={28} fill="white" style={{ color: 'white' }} />
                    ) : (
                      <div
                        style={{
                          width: '56px',
                          height: '56px',
                          borderRadius: '50%',
                          background: '#ef4444',
                        }}
                      />
                    )}
                  </button>

                  {/* Timer when recording */}
                  {isRecording && (
                    <p style={{ fontSize: '18px', fontWeight: '600', color: 'var(--gray-900)' }}>
                      {formatTime(recordingTime)}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
